import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiFilePlus, FiPlus, FiTrash2, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Badge, DataTable, Modal, ConfirmDialog, Spinner } from '../../components/common';
import CmsField from '../cms/components/CmsField';
import ClientFormModal from './components/ClientFormModal';
import { billingClientService } from '../../services/billingClientService';
import { invoiceService } from '../../services/invoiceService';
import { getApiError, getApiFieldErrors } from '../../services/cmsService';
import { InvoiceStatusBadge, formatDMY, formatINR } from './billingUtils';
import './Billing.css';

const PROJECTS_PER_PAGE = 50;
const INVOICES_PER_PAGE = 15;

const PROJECT_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: '', label: 'All' },
];

const emptyProject = () => ({ description: '', designCode: '', hsnCode: '', taxableAmount: '' });

const BillingClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState('projects');

  const [projectFilter, setProjectFilter] = useState('pending');
  const [projects, setProjects] = useState({ items: [], total: 0 });
  const [projectPage, setProjectPage] = useState(1);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectForm, setProjectForm] = useState(null); // { id?, ...fields }
  const [projectErrors, setProjectErrors] = useState({});
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [invoices, setInvoices] = useState({ items: [], total: 0 });
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const loadClient = useCallback(async () => {
    try {
      setClient(await billingClientService.getById(id));
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load the client'));
      navigate('/billing/clients', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const params = { page: projectPage, per_page: PROJECTS_PER_PAGE };
      if (projectFilter) params.invoice_status = projectFilter;
      setProjects(await billingClientService.getProjects(id, params));
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load projects'));
    } finally {
      setProjectsLoading(false);
    }
  }, [id, projectFilter, projectPage]);

  useEffect(() => {
    setLoading(true);
    loadClient();
  }, [loadClient]);

  useEffect(() => {
    if (tab === 'projects') loadProjects();
  }, [tab, loadProjects]);

  useEffect(() => {
    if (tab !== 'invoices') return undefined;
    let cancelled = false;
    setInvoicesLoading(true);
    invoiceService
      .getAll({ billing_client_id: id, page: invoicePage, per_page: INVOICES_PER_PAGE })
      .then((res) => !cancelled && setInvoices(res))
      .catch((error) => !cancelled && toast.error(getApiError(error, 'Failed to load invoices')))
      .finally(() => !cancelled && setInvoicesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, id, invoicePage]);

  const refreshAll = () => {
    loadClient();
    loadProjects();
  };

  const openProjectForm = (project = null) => {
    setProjectErrors({});
    setProjectForm(project ? { ...project } : emptyProject());
  };

  const setProjectField = (field, value) => {
    setProjectForm((prev) => ({ ...prev, [field]: value }));
    setProjectErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const saveProject = async () => {
    const next = {};
    if (!projectForm.description.trim()) next.description = 'Description is required';
    if (!String(projectForm.taxableAmount).trim()) next.taxableAmount = 'Taxable amount is required';
    if (Object.keys(next).length) {
      setProjectErrors(next);
      return;
    }
    setProjectSaving(true);
    try {
      if (projectForm.id) await billingClientService.updateProject(projectForm.id, projectForm);
      else await billingClientService.createProject(id, projectForm);
      toast.success(projectForm.id ? 'Project updated' : 'Project added');
      setProjectForm(null);
      refreshAll();
    } catch (error) {
      const api = getApiFieldErrors(error);
      setProjectErrors({
        description: api.description || api.project,
        designCode: api.design_code,
        hsnCode: api.hsn_code,
        taxableAmount: api.taxable_amount,
      });
      toast.error(getApiError(error, 'Failed to save the project'));
    } finally {
      setProjectSaving(false);
    }
  };

  const deleteProject = async () => {
    setDeleting(true);
    try {
      await billingClientService.deleteProject(projectToDelete.id);
      toast.success('Project deleted');
      refreshAll();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete the project'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !client) {
    return (
      <div className="bl-center">
        <Spinner size="large" />
      </div>
    );
  }

  const totals = client.totals ?? {};

  const projectColumns = [
    {
      header: 'Description',
      render: (row) => (
        <span className="bl-cell-stack">
          <span>{row.description}</span>
          {row.designCode && <span className="bl-cell-sub">{row.designCode}</span>}
        </span>
      ),
    },
    { header: 'HSN', width: '100px', render: (row) => <span className="bl-mono">{row.hsnCode || '—'}</span> },
    { header: 'Taxable', align: 'right', render: (row) => <span className="bl-amount">{formatINR(row.taxableAmount)}</span> },
    {
      header: 'Status',
      render: (row) =>
        row.invoiceStatus === 'invoiced' ? (
          <span className="bl-cell-stack">
            <Badge variant="success" size="small">Invoiced</Badge>
            {row.invoice && (
              <Link className="bl-link bl-cell-sub" to={`/billing/invoices/${row.invoice.id}`}>
                {row.invoice.number || 'View invoice'}
              </Link>
            )}
          </span>
        ) : (
          <Badge variant="orange" size="small">Pending</Badge>
        ),
    },
    {
      header: 'Actions',
      width: '110px',
      align: 'center',
      render: (row) =>
        row.invoiceStatus === 'pending' ? (
          <div className="bl-actions">
            <button type="button" className="action-btn edit" title="Edit" onClick={() => openProjectForm(row)}>
              <FiEdit2 />
            </button>
            <button type="button" className="action-btn delete" title="Delete" onClick={() => setProjectToDelete(row)}>
              <FiTrash2 />
            </button>
          </div>
        ) : (
          <span className="bl-muted" title="Invoiced projects are locked">Locked</span>
        ),
    },
  ];

  const invoiceColumns = [
    {
      header: 'Invoice No',
      render: (row) => (
        <Link className="bl-link" to={`/billing/invoices/${row.id}`}>{row.number || 'Draft'}</Link>
      ),
    },
    { header: 'Date', render: (row) => formatDMY(row.invoiceDate) },
    { header: 'Total', align: 'right', render: (row) => <span className="bl-amount">{formatINR(row.totalAmount)}</span> },
    { header: 'Received', align: 'right', render: (row) => <span className="bl-amount">{formatINR(row.receivedAmount)}</span> },
    { header: 'Balance', align: 'right', render: (row) => <span className="bl-amount">{formatINR(row.balanceAmount)}</span> },
    { header: 'Status', render: (row) => <InvoiceStatusBadge status={row.status} isOverdue={row.isOverdue} /> },
  ];

  return (
    <div className="bl-page">
      <Link to="/billing/clients" className="bl-back">
        <FiArrowLeft /> Back to billing clients
      </Link>

      <Card
        title={client.name}
        subtitle={[client.clientCode, client.isActive ? 'Active' : 'Inactive'].filter(Boolean).join(' · ')}
        action={
          <div className="bl-header-actions">
            <Button variant="outline" icon={<FiEdit2 />} onClick={() => setEditOpen(true)}>Edit</Button>
            <Button icon={<FiFilePlus />} onClick={() => navigate(`/billing/invoices/new?client_id=${client.id}`)}>
              Create invoice
            </Button>
          </div>
        }
      >
        <dl className="bl-info-grid">
          <div>
            <dt>GSTIN</dt>
            <dd className="bl-mono">{client.gstin || '—'}</dd>
          </div>
          <div>
            <dt>State</dt>
            <dd>{client.stateCode ? `${client.stateCode} — ${client.stateName}` : '—'}</dd>
          </div>
          <div>
            <dt>Contact person</dt>
            <dd>{client.contactPerson || '—'}</dd>
          </div>
          <div>
            <dt>Email / phone</dt>
            <dd>{[client.email, client.phone].filter(Boolean).join(' · ') || '—'}</dd>
          </div>
          <div className="bl-span-all">
            <dt>Address</dt>
            <dd>{client.address || '—'}</dd>
          </div>
          {client.notes && (
            <div className="bl-span-all">
              <dt>Notes</dt>
              <dd>{client.notes}</dd>
            </div>
          )}
        </dl>

        {!client.stateCode && (
          <div className="bl-banner warning" style={{ marginTop: '1rem' }}>
            <FiAlertTriangle />
            <div className="bl-banner-body">No GSTIN or state — invoices for this client can’t be issued until one is added.</div>
          </div>
        )}
      </Card>

      <div className="bl-stats">
        <div className="bl-stat">
          <span className="bl-stat-label">Invoiced</span>
          <span className="bl-stat-value">{formatINR(totals.invoiced)}</span>
        </div>
        <div className="bl-stat tone-success">
          <span className="bl-stat-label">Received</span>
          <span className="bl-stat-value">{formatINR(totals.received)}</span>
        </div>
        <div className="bl-stat tone-warning">
          <span className="bl-stat-label">Outstanding</span>
          <span className="bl-stat-value">{formatINR(totals.outstanding)}</span>
        </div>
        <div className="bl-stat tone-orange">
          <span className="bl-stat-label">Pending projects</span>
          <span className="bl-stat-value">{formatINR(totals.pendingProjectsValue)}</span>
          <span className="bl-stat-hint">{client.pendingProjects.length} not yet invoiced (taxable value)</span>
        </div>
      </div>

      <Card>
        <div className="bl-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'projects'} className={`bl-tab ${tab === 'projects' ? 'is-active' : ''}`} onClick={() => setTab('projects')}>
            Projects
          </button>
          <button type="button" role="tab" aria-selected={tab === 'invoices'} className={`bl-tab ${tab === 'invoices' ? 'is-active' : ''}`} onClick={() => setTab('invoices')}>
            Invoices
          </button>
        </div>

        {tab === 'projects' ? (
          <DataTable
            columns={projectColumns}
            data={projects.items}
            loading={projectsLoading}
            searchable={false}
            pagination
            pageSize={PROJECTS_PER_PAGE}
            totalItems={projects.total}
            currentPage={projectPage}
            onPageChange={setProjectPage}
            emptyMessage={projectFilter === 'pending' ? 'No pending projects.' : 'No projects.'}
            actions={
              <div className="bl-header-actions">
                <div className="bl-chip-group" role="group" aria-label="Filter projects">
                  {PROJECT_FILTERS.map((f) => (
                    <button
                      key={f.value || 'all'}
                      type="button"
                      className={`bl-chip ${projectFilter === f.value ? 'is-active' : ''}`}
                      aria-pressed={projectFilter === f.value}
                      onClick={() => {
                        setProjectFilter(f.value);
                        setProjectPage(1);
                      }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <Button size="small" icon={<FiPlus />} onClick={() => openProjectForm()}>Add project</Button>
              </div>
            }
          />
        ) : (
          <DataTable
            columns={invoiceColumns}
            data={invoices.items}
            loading={invoicesLoading}
            searchable={false}
            pagination
            pageSize={INVOICES_PER_PAGE}
            totalItems={invoices.total}
            currentPage={invoicePage}
            onPageChange={setInvoicePage}
            emptyMessage="No invoices for this client yet."
          />
        )}
      </Card>

      <ClientFormModal isOpen={editOpen} onClose={() => setEditOpen(false)} client={client} onSaved={() => loadClient()} />

      <Modal
        isOpen={Boolean(projectForm)}
        onClose={() => setProjectForm(null)}
        title={projectForm?.id ? 'Edit project' : 'Add project'}
        size="medium"
        closeOnOverlay={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setProjectForm(null)} disabled={projectSaving}>Cancel</Button>
            <Button onClick={saveProject} loading={projectSaving}>{projectForm?.id ? 'Save' : 'Add project'}</Button>
          </>
        }
      >
        {projectForm && (
          <div className="bl-form-grid">
            <div className="bl-span-all">
              <CmsField
                id="pj-desc"
                type="textarea"
                label="Description"
                required
                rows={3}
                value={projectForm.description}
                onChange={(v) => setProjectField('description', v)}
                error={projectErrors.description}
              />
            </div>
            <CmsField id="pj-code" label="Design code" placeholder="20- DD/GM" value={projectForm.designCode} onChange={(v) => setProjectField('designCode', v)} error={projectErrors.designCode} />
            <CmsField id="pj-hsn" label="HSN/SAC" placeholder="Default from settings" value={projectForm.hsnCode} onChange={(v) => setProjectField('hsnCode', v)} error={projectErrors.hsnCode} />
            <CmsField
              id="pj-amount"
              label="Taxable amount (₹)"
              required
              value={projectForm.taxableAmount}
              onChange={(v) => setProjectField('taxableAmount', v)}
              error={projectErrors.taxableAmount}
              inputProps={{ inputMode: 'decimal' }}
            />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(projectToDelete)}
        onClose={() => setProjectToDelete(null)}
        onConfirm={deleteProject}
        title="Delete project"
        message={`Delete "${projectToDelete?.description}"? Draft invoice lines that used it stay on the draft as ad-hoc lines.`}
        confirmText="Delete project"
        loading={deleting}
      />
    </div>
  );
};

export default BillingClientDetailPage;
